import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
// zod4Resolver, never zodResolver: the latter reads `parsed.error.errors`,
// which zod 4 does not have, so validation fails silently.
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { useAccounts, useCreateAccount, useUpdateAccount } from '../api/hooks.js';
import type { Account } from '../api/hooks.js';

/**
 * The chart of accounts.
 *
 * Read-mostly by design. Accounts come from Erhvervsstyrelsen's standard chart,
 * which is mandatory from January 2026, so the common operations are renaming
 * one and taking one out of use — not inventing new ones.
 *
 * **An account is never deleted.** It has postings against it, and those are
 * immutable; removing the account would orphan them. Deactivating hides it from
 * the posting form and leaves the history intact, which is why the control is a
 * switch labelled "Aktiv" rather than a delete button.
 *
 * **Creating one is possible, and was missing.** Read-mostly was read as
 * read-and-edit-only, and the page shipped with no way to add an account at
 * all — while /bank told people to use codes 6481-6489 for a second bank
 * account, which do not exist until somebody creates them. A real user
 * followed that advice and hit `account_not_found` with nowhere in the
 * application to fix it. The standard chart argument holds for revenue and
 * expense accounts; it never held for a second bank account.
 */

const TYPE_LABELS: Record<Account['type'], MessageKey> = {
  asset: 'accounts.type.asset',
  liability: 'accounts.type.liability',
  equity: 'accounts.type.equity',
  revenue: 'accounts.type.revenue',
  expense: 'accounts.type.expense',
};

/** The Select's options, in the reader's language. */
function typeOptions(t: (key: MessageKey) => string) {
  return Object.entries(TYPE_LABELS).map(([value, key]) => ({
    value,
    label: t(key),
  }));
}

const TYPE_COLORS: Record<Account['type'], string> = {
  asset: 'blue',
  liability: 'orange',
  equity: 'grape',
  revenue: 'green',
  expense: 'red',
};

export function AccountsPage(): ReactNode {
  const t = useT();
  const [search, setSearch] = useState('');
  const [type, setType] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const accounts = useAccounts({
    search: search.trim() === '' ? undefined : search.trim(),
    type: type ?? undefined,
    includeInactive,
  });

  const [createOpened, createModal] = useDisclosure(false);

  function startEdit(account: Account): void {
    setEditing(account);
    open();
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>{t('accounts.title')}</Title>
        <Button onClick={createModal.open}>{t('accounts.new')}</Button>
      </Group>

      <Group align="flex-end" wrap="wrap">
        <TextInput
          label={t('accounts.search')}
          placeholder={t('accounts.searchPlaceholder')}
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
          }}
          maw={260}
        />
        <Select
          label={t('accounts.type')}
          placeholder={t('accounts.allTypes')}
          clearable
          value={type}
          onChange={setType}
          data={typeOptions(t)}
          maw={200}
        />
        <Switch
          label={t('accounts.showInactive')}
          checked={includeInactive}
          onChange={(event) => {
            setIncludeInactive(event.currentTarget.checked);
          }}
          mb={8}
        />
      </Group>

      {accounts.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {accounts.isError && <ErrorMessage error={accounts.error} />}

      {accounts.isSuccess && accounts.data.items.length === 0 && (
        <Text c="dimmed">{t('accounts.none')}</Text>
      )}

      {accounts.isSuccess && accounts.data.items.length > 0 && (
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 100 }}>{t('accounts.col.number')}</Table.Th>
                <Table.Th>{t('accounts.col.name')}</Table.Th>
                <Table.Th style={{ width: 130 }}>{t('accounts.type')}</Table.Th>
                <Table.Th style={{ width: 90 }}>{t('accounts.col.vat')}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t('accounts.col.standard')}</Table.Th>
                <Table.Th style={{ width: 90 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {accounts.data.items.map((account) => (
                <Table.Tr key={account.id} opacity={account.isActive ? 1 : 0.55}>
                  <Table.Td>
                    <Text ff="monospace">{account.code}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text>{account.name}</Text>
                      {!account.isActive && (
                        <Badge size="xs" color="gray" variant="light">
                          {t('accounts.inactive')}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={TYPE_COLORS[account.type]} variant="light" size="sm">
                      {t(TYPE_LABELS[account.type])}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {account.vatCode ?? ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" ff="monospace">
                      {account.standardChartCode ?? ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      onClick={() => {
                        startEdit(account);
                      }}
                    >
                      {t('accounts.edit')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <EditAccountModal
        account={editing}
        opened={opened}
        onClose={() => {
          close();
          setEditing(null);
        }}
      />

      <CreateAccountModal opened={createOpened} onClose={createModal.close} />
    </Stack>
  );
}

function createSchema(t: (key: MessageKey) => string) {
  return z.object({
    code: z
      .string()
      .trim()
      .min(1, t('accounts.create.numberRequired'))
      .max(32)
      .regex(/^[0-9]+$/u, t('accounts.create.numberDigits')),
    name: z.string().trim().min(1, t('accounts.create.nameRequired')).max(255),
    type: z.enum(['asset', 'liability', 'equity', 'revenue', 'expense']),
    vatCode: z.string().trim().max(16),
  });
}

type CreateValues = z.infer<ReturnType<typeof createSchema>>;

/**
 * Add an account to the chart.
 *
 * The screen that was missing. It says what a hand-made account does and does
 * not inherit, because the standard chart is what the reports group by: an
 * account whose number falls inside a range the chart already sums rolls up on
 * its own, and one outside those ranges does not appear in the reports at all.
 * That is not obvious from a form with four fields.
 */
function CreateAccountModal({
  opened,
  onClose,
}: {
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const create = useCreateAccount();

  const form = useForm<CreateValues>({
    mode: 'uncontrolled',
    initialValues: { code: '', name: '', type: 'asset', vatCode: '' },
    validate: zod4Resolver(createSchema(t)),
  });

  function handleSubmit(values: CreateValues): void {
    create.mutate(
      {
        code: values.code.trim(),
        name: values.name.trim(),
        type: values.type,
        // Empty means "no VAT code", which is absent rather than an empty
        // string -- the API's pattern would refuse "".
        ...(values.vatCode.trim() === '' ? {} : { vatCode: values.vatCode.trim() }),
      },
      {
        onSuccess: (account) => {
          notifications.show({
            color: 'green',
            title: t('accounts.create.created'),
            message: `${account.code} ${account.name}`,
          });
          form.reset();
          onClose();
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('accounts.create.title')}>
      {/*
        noValidate: the browser's own constraint validation blocks the submit
        event before Mantine sees it, so the Zod schema never runs and the user
        gets a browser tooltip in the browser's language.
      */}
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Alert color="gray" variant="light" title={t('accounts.create.beforeTitle')}>
            {t('accounts.create.before')}
            <br />
            <br />
            {t('accounts.create.bankHint')}
          </Alert>

          <TextInput
            label={t('accounts.create.number')}
            placeholder="6481"
            withAsterisk
            key={form.key('code')}
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('accounts.col.name')}
            placeholder={t('accounts.create.namePlaceholder')}
            withAsterisk
            key={form.key('name')}
            {...form.getInputProps('name')}
          />
          <Select
            label={t('accounts.type')}
            withAsterisk
            allowDeselect={false}
            data={typeOptions(t)}
            key={form.key('type')}
            {...form.getInputProps('type')}
          />
          <TextInput
            label={t('accounts.create.vatCode')}
            description={t('accounts.create.vatCodeHelp')}
            key={form.key('vatCode')}
            {...form.getInputProps('vatCode')}
          />

          {create.isError && (
            <ErrorMessage error={create.error} title={t('accounts.create.failed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function EditAccountModal({
  account,
  opened,
  onClose,
}: {
  readonly account: Account | null;
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const update = useUpdateAccount();
  const [name, setName] = useState('');
  const [vatCode, setVatCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Fill the fields from the account being edited, once per account, without an
  // effect — deriving during render keeps the modal in step with the row that
  // opened it even if the list refetches underneath.
  if (account !== null && loadedFor !== account.id) {
    setLoadedFor(account.id);
    setName(account.name);
    setVatCode(account.vatCode ?? '');
    setIsActive(account.isActive);
  }

  function handleSave(): void {
    if (account === null) {
      return;
    }
    update.mutate(
      {
        id: account.id,
        patch: {
          name: name.trim(),
          vatCode: vatCode.trim() === '' ? null : vatCode.trim(),
          isActive,
        },
      },
      {
        onSuccess: (saved) => {
          notifications.show({
            color: 'green',
            title: t('accounts.edit.saved'),
            message: `${saved.code} — ${saved.name}`,
          });
          onClose();
        },
        // No error notification: the modal stays open and renders the refusal
        // inline, with the request id. A toast saying the same thing would be
        // the same message twice, and the toast is the one that disappears.
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={account === null ? '' : t('accounts.edit.title', { code: account.code })}
    >
      <Stack gap="md">
        <TextInput
          label={t('accounts.col.name')}
          value={name}
          onChange={(event) => {
            setName(event.currentTarget.value);
          }}
          required
        />
        <TextInput
          label={t('accounts.create.vatCode')}
          description={t('accounts.edit.vatCodeHelp')}
          value={vatCode}
          onChange={(event) => {
            setVatCode(event.currentTarget.value);
          }}
        />
        <Switch
          label={t('accounts.edit.active')}
          description={t('accounts.edit.activeHelp')}
          checked={isActive}
          onChange={(event) => {
            setIsActive(event.currentTarget.checked);
          }}
        />

        {update.isError && (
          <ErrorMessage error={update.error} title={t('accounts.edit.failed')} />
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            loading={update.isPending}
            disabled={name.trim() === ''}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
